/**
 * ドロップダウンセレクターの共通ロジック
 * 検索ページ(searchMain.js)と検索パネル(searchPanel.js)で共有
 *
 * @param {HTMLElement} rootEl  - ドロップダウンを含むルート要素
 * @param {Object}      options
 * @param {string}      options.prefix          - セレクタプレフィックス（検索ページ: '', パネル: 'sp-'）
 * @param {Function}    options.onOptionChange   - オプション変更後のコールバック ({ target, label, value })
 * @param {Function}    options.onDropdownToggle - ドロップダウン開閉直後に同期で呼ぶコールバック（横ズレ補正用）
 */
export default class DropdownSelector {
  constructor(rootEl, options = {}) {
    this.root = rootEl;
    const p = options.prefix || "";
    this.prefix = p;
    this.onOptionChange = options.onOptionChange || null;
    this.onDropdownToggle = options.onDropdownToggle || null;

    // プレフィックス付きセレクタ
    this.sel = {
      searchoptions: ".js-" + p + "searchoptions",
      option: ".js-" + p + "option",
      optionAll: ".js-" + p + "option-all",
      optionCategory: ".js-" + p + "option-category",
      default: ".js-" + p + "default",
      closeoption: ".js-" + p + "closeoption",
      label: ".js-" + p + "label",
      input: ".js-" + p + "input",
    };

    this.setupDropdowns();
    this.setupCategories();
    this.setupOptionItems();
    this.setupCloseButtons();
    this.setupOutsideClick();
    this.setupFocusEvent();
  }

  // =========================================
  // ドロップダウン開閉
  // =========================================

  setupDropdowns() {
    var self = this;
    var titles = this.root.querySelectorAll(this.sel.searchoptions);
    titles.forEach(function (title) {
      title.addEventListener("click", function (e) {
        e.preventDefault();
        var targetSelector = title.getAttribute("data-target");
        if (!targetSelector) return;

        self.closeAllDropdowns(targetSelector);
        self.closeAllCategories();

        var optionsDom = self.root.querySelector(targetSelector);
        if (!optionsDom) return;

        var isActive = optionsDom.classList.toggle("active");
        if (isActive) {
          title.classList.add("open");
          optionsDom.focus();
        } else {
          title.classList.remove("open");
          title.focus();
        }
        // クラストグルと同じ実行タイミングで補正を当て、:has() の overflow 切替と
        // 同一レイアウトで解決させる（非同期だとWin実機で1フレーム横ズレが残る）
        if (self.onDropdownToggle) self.onDropdownToggle();
      });
    });
  }

  /**
   * 全ドロップダウンを閉じる（exclude で指定したものは除外）
   */
  closeAllDropdowns(exclude) {
    var self = this;
    var titles = this.root.querySelectorAll(this.sel.searchoptions);
    titles.forEach(function (title) {
      var targetSelector = title.getAttribute("data-target");
      if (targetSelector === exclude) return;
      if (targetSelector) {
        title.classList.remove("open");
        var optionsDom = self.root.querySelector(targetSelector);
        if (optionsDom) optionsDom.classList.remove("active");
      }
    });
    // 閉じる全経路（外部クリック・ESC・閉じるボタン・他項目を開いた時）を集約。
    // クラス除去と同期で補正を更新し、閉じる瞬間の横ズレを防ぐ
    if (self.onDropdownToggle) self.onDropdownToggle();
  }

  /**
   * 全カテゴリアコーディオンを閉じる
   */
  closeAllCategories() {
    var self = this;
    var categories = this.root.querySelectorAll(this.sel.optionCategory);
    categories.forEach(function (cat) {
      var target = cat.getAttribute("data-category");
      if (target) {
        var dom = self.root.querySelector(target);
        if (dom) dom.classList.remove("open");
      }
      cat.setAttribute("aria-expanded", "false");
    });
  }

  // =========================================
  // カテゴリアコーディオン
  // =========================================

  /**
   * role="button" を持つ <a> は Enter でしか発火しないため、
   * WAI-ARIA button パターンに合わせて Space キーでも発火できるようにする
   */
  bindActivate(el, handler) {
    el.addEventListener("click", function (e) {
      e.preventDefault();
      handler(e);
    });
    el.addEventListener("keydown", function (e) {
      if (e.key === " " || e.key === "Spacebar") {
        e.preventDefault();
        handler(e);
      }
    });
  }

  setupCategories() {
    var self = this;
    var categories = this.root.querySelectorAll(this.sel.optionCategory);
    categories.forEach(function (cat) {
      self.bindActivate(cat, function () {
        var target = cat.getAttribute("data-category");
        var targetDom = self.root.querySelector(target);
        if (!targetDom) return;
        var isOpen = targetDom.classList.toggle("open");
        cat.setAttribute("aria-expanded", isOpen ? "true" : "false");
      });
    });
  }

  // =========================================
  // オプション選択
  // =========================================

  setupOptionItems() {
    var self = this;
    var options = this.root.querySelectorAll(this.sel.option);
    options.forEach(function (option) {
      self.bindActivate(option, function () {
        if (option.classList.contains("js-" + self.prefix + "option-all")) {
          self.selectGroupAll(option);
        } else {
          self.clearGroupAll(option);
        }
        self.execOptionItem(option);
      });
    });
  }

  setupCloseButtons() {
    var self = this;
    var closes = this.root.querySelectorAll(this.sel.closeoption);
    closes.forEach(function (closeBtn) {
      self.bindActivate(closeBtn, function () {
        self.closeAllDropdowns();
      });
    });
  }

  /**
   * 「◯◯すべて」選択時：グループ内の個別選択を解除
   */
  selectGroupAll(target) {
    var self = this;
    var groupOptions = target.parentNode.querySelectorAll(this.sel.option);
    groupOptions.forEach(function (option) {
      if (
        !option.classList.contains("js-" + self.prefix + "option-all") &&
        option.classList.contains("active")
      ) {
        option.classList.remove("active");
        option.setAttribute("aria-pressed", "false");
      }
    });
  }

  /**
   * 個別選択時：「◯◯すべて」の選択を解除
   */
  clearGroupAll(target) {
    var allName = target.getAttribute("data-groupall");
    if (!allName) return;
    var groupAll = this.root.querySelector(allName);
    if (groupAll && groupAll.classList.contains("active")) {
      groupAll.classList.remove("active");
      groupAll.setAttribute("aria-pressed", "false");
    }
  }

  /**
   * オプション選択実行
   * @param {HTMLElement} option     - クリックされたオプション要素
   * @param {boolean}     clearOthers - 他の選択肢をすべて解除するか
   */
  execOptionItem(option, clearOthers) {
    var targetSelector = option.getAttribute("data-target");
    var value = option.getAttribute("data-value");
    var targetDom = this.root.querySelector(targetSelector);
    if (!targetDom) return;

    var defaultOpt = targetDom.querySelector(this.sel.default);

    if (clearOthers) {
      this.clearOptionsInGroup(targetDom);
    }

    if (value === "") {
      // 「すべて」選択 → 全解除してデフォルト有効化
      this.clearOptionsInGroup(targetDom);
      if (defaultOpt) {
        defaultOpt.classList.add("active");
        defaultOpt.setAttribute("aria-pressed", "true");
      }
    } else {
      // デフォルト解除
      if (defaultOpt) {
        defaultOpt.classList.remove("active");
        defaultOpt.setAttribute("aria-pressed", "false");
      }

      // トグル
      var isActive = option.classList.toggle("active");
      option.setAttribute("aria-pressed", isActive ? "true" : "false");

      // 全解除されていたらデフォルトに戻す
      var activeOptions = targetDom.querySelectorAll(this.sel.option + ".active");
      var hasActive = false;
      activeOptions.forEach(function (opt) {
        if (!opt.classList.contains("js-" + this.prefix + "default")) {
          hasActive = true;
        }
      }.bind(this));
      if (!hasActive && defaultOpt) {
        defaultOpt.classList.add("active");
        defaultOpt.setAttribute("aria-pressed", "true");
      }
    }

    // ラベルとhidden input更新
    this.updateDisplay(targetDom);

    // コールバック
    if (this.onOptionChange) {
      this.onOptionChange({
        target: targetSelector,
        label: option.getAttribute("data-label"),
        value: value,
      });
    }
  }

  /**
   * グループ内の全オプションを解除
   */
  clearOptionsInGroup(targetDom) {
    var options = targetDom.querySelectorAll(this.sel.option);
    options.forEach(function (opt) {
      opt.classList.remove("active");
      opt.setAttribute("aria-pressed", "false");
    });
  }

  /**
   * ラベルとhidden inputをactive状態から再構築
   */
  updateDisplay(targetDom) {
    var activeOptions = targetDom.querySelectorAll(
      this.sel.option + ".active:not(" + this.sel.default + ")"
    );
    var labelEl = targetDom.querySelector(this.sel.label);
    var inputEl = targetDom.querySelector(this.sel.input);
    var defaultOpt = targetDom.querySelector(this.sel.default);

    if (activeOptions.length === 0) {
      if (labelEl) {
        labelEl.textContent = defaultOpt
          ? defaultOpt.getAttribute("data-label")
          : "すべて";
      }
      if (inputEl) inputEl.value = "";
      return;
    }

    var labelNodes = [];
    var values = [];
    activeOptions.forEach(function (opt) {
      var dataLabel = opt.getAttribute("data-label");
      // optionに色玉（背景色付きspan）があれば、ラベルに含める
      var colorChip = opt.querySelector('span[style*="background"]');
      var nameSpan = document.createElement("span");
      if (colorChip) {
        nameSpan.className = "color_name";
        nameSpan.textContent = dataLabel;
        var wrapper = document.createDocumentFragment();
        wrapper.appendChild(colorChip.cloneNode(true));
        wrapper.appendChild(nameSpan);
        labelNodes.push(wrapper);
      } else {
        nameSpan.className = "label";
        nameSpan.textContent = dataLabel;
        labelNodes.push(nameSpan);
      }
      values.push(opt.getAttribute("data-value"));
    });

    if (labelEl) {
      labelEl.innerHTML = "";
      labelNodes.forEach(function (node, i) {
        if (i > 0) labelEl.appendChild(document.createTextNode(","));
        labelEl.appendChild(node);
      });
    }
    if (inputEl) inputEl.value = values.join(",");
  }

  /**
   * 全ドロップダウンをデフォルト状態にリセット
   */
  clearAllSelections() {
    var self = this;
    var defaults = this.root.querySelectorAll(this.sel.default);
    defaults.forEach(function (defaultOpt) {
      self.execOptionItem(defaultOpt, true);
    });
  }

  // =========================================
  // 外部クリックで閉じる（PC）
  // =========================================

  setupOutsideClick() {
    if (window.innerWidth <= 768) return;
    var self = this;
    document.addEventListener("click", function (e) {
      // トリガーボタンをクリックした場合は setupDropdowns 側で処理
      var btn = e.target.closest(self.sel.searchoptions);
      if (btn && self.root.contains(btn)) return;

      // ドロップダウンパネル内をクリックした場合は無視
      var titles = self.root.querySelectorAll(self.sel.searchoptions);
      var isInPanel = false;
      titles.forEach(function (title) {
        var target = title.getAttribute("data-target");
        if (target) {
          var panel = self.root.querySelector(target);
          if (panel && panel.contains(e.target)) {
            isInPanel = true;
          }
        }
      });

      if (!isInPanel) {
        self.closeAllDropdowns();
      }
    });
  }

  // =========================================
  // ESCキーで閉じる
  // =========================================

  setupFocusEvent() {
    var self = this;
    var titles = this.root.querySelectorAll(this.sel.searchoptions);
    titles.forEach(function (title) {
      var targetSelector = title.getAttribute("data-target");
      if (!targetSelector) return;
      var optionsDom = self.root.querySelector(targetSelector);
      if (!optionsDom) return;

      optionsDom.addEventListener("keydown", function (e) {
        if (e.key === "Escape") {
          self.closeAllDropdowns();
        }
      });
    });
  }
}
